import type { ReactNode } from 'react';
import type { Permission } from '@asi/shared';
import { usePermissions } from '@/hooks/usePermissions';

interface PermissionGateProps {
  /** Render children only if user has ALL of these permissions */
  require?: Permission | Permission[];
  /** Render children if user has ANY of these permissions */
  requireAny?: Permission[];
  /** Fallback UI when permission check fails (defaults to nothing) */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditionally renders children based on the current user's permissions.
 *
 * Usage:
 *   <PermissionGate require="users.create">
 *     <CreateUserButton />
 *   </PermissionGate>
 *
 *   <PermissionGate requireAny={['tickets.create', 'tickets.update']}>
 *     <EditButton />
 *   </PermissionGate>
 */
export function PermissionGate({ require, requireAny, fallback = null, children }: PermissionGateProps) {
  const { can, canAny } = usePermissions();

  let allowed = true;

  if (require !== undefined) {
    const perms = Array.isArray(require) ? require : [require];
    allowed = perms.every((p) => can(p));
  }

  if (allowed && requireAny !== undefined) {
    allowed = canAny(requireAny);
  }

  return allowed ? <>{children}</> : <>{fallback}</>;
}
